import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, Loader2 } from 'lucide-react';

const AUDIENCE_SIZES = ['<1k', '1k-10k', '10k-50k', '50k-100k', '100k+'] as const;
const AUDIENCE_TYPES = ['Brokers', 'Carriers', 'Shippers', 'Mixed freight / logistics'] as const;
const INTERESTS = ['Sponsored content', 'Ads', 'Newsletter placement', 'Other'] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

const MediaPartnershipDialog: React.FC<Props> = ({ open, onOpenChange, source = 'partner_program_media' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('');
  const [links, setLinks] = useState('');
  const [audienceSize, setAudienceSize] = useState('');
  const [audienceType, setAudienceType] = useState('');
  const [interest, setInterest] = useState('');
  const [comments, setComments] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const valid =
    name.trim().length > 1 &&
    EMAIL_RE.test(email.trim()) &&
    platform.trim().length > 1;

  const reset = () => {
    setName(''); setEmail(''); setPlatform(''); setLinks(''); setAudienceSize('');
    setAudienceType(''); setInterest(''); setComments(''); setWebsite('');
    setError(null); setDone(false); setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('submit-media-partnership', {
        body: {
          name: name.trim(),
          email: email.trim(),
          platform: platform.trim(),
          links: links.trim(),
          audience_size: audienceSize,
          audience_type: audienceType,
          partnership_interest: interest,
          comments: comments.trim(),
          website: website.trim(),
          source,
        },
      });
      const payload = data as { ok?: boolean; error?: string } | null;
      if (fnErr || !payload?.ok) {
        setError(payload?.error || 'Something went wrong. Please try again or email support@lanerunner.com.');
        return;
      }
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again or email support@lanerunner.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-[560px] overflow-y-auto">
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12" style={{ color: '#02857E' }} />
            <DialogTitle className="mt-4 text-lg">Inquiry received</DialogTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              Thanks — we'll be in touch about partnering.
            </p>
            <Button className="mt-6" onClick={() => handleOpenChange(false)}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Talk to LaneRunner</DialogTitle>
              <DialogDescription>
                Tell us about your audience and how you'd like to work together.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="mp-name">Name / Company *</Label>
                  <Input id="mp-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mp-email">Email *</Label>
                  <Input id="mp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mp-platform">Platform or channel *</Label>
                  <Input
                    id="mp-platform"
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    placeholder="YouTube, podcast, newsletter, LinkedIn, community…"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mp-links">Website / social links <span className="text-muted-foreground">(optional)</span></Label>
                  <Input id="mp-links" value={links} onChange={(e) => setLinks(e.target.value)} placeholder="https://…" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mp-size">Approximate audience size <span className="text-muted-foreground">(optional)</span></Label>
                  <Select value={audienceSize} onValueChange={setAudienceSize}>
                    <SelectTrigger id="mp-size"><SelectValue placeholder="Select one" /></SelectTrigger>
                    <SelectContent>
                      {AUDIENCE_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mp-type">Audience type <span className="text-muted-foreground">(optional)</span></Label>
                  <Select value={audienceType} onValueChange={setAudienceType}>
                    <SelectTrigger id="mp-type"><SelectValue placeholder="Select one" /></SelectTrigger>
                    <SelectContent>
                      {AUDIENCE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mp-interest">Partnership interest <span className="text-muted-foreground">(optional)</span></Label>
                <Select value={interest} onValueChange={setInterest}>
                  <SelectTrigger id="mp-interest"><SelectValue placeholder="Select one" /></SelectTrigger>
                  <SelectContent>
                    {INTERESTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mp-comments">Comments <span className="text-muted-foreground">(optional)</span></Label>
                <Textarea id="mp-comments" rows={4} maxLength={2000} value={comments} onChange={(e) => setComments(e.target.value)} />
              </div>

              {/* honeypot — hidden from humans */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="mp-website">Website</label>
                <input id="mp-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
              )}

              <Button type="submit" className="w-full" disabled={!valid || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitting ? 'Submitting…' : 'Submit inquiry'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MediaPartnershipDialog;
